import { useCallback, useEffect, useRef, useState } from "react";
import { portalApi, refreshSession } from "../api";
import { errorText, statusOf } from "../errors";
import { count, languageName, remaining, runtime, scoreText } from "../format";
import { useI18n, type Translate } from "../i18n";
import { usePortal } from "../state";
import type {
  Lang,
  OrgPlanView,
  Rental,
  TitleDetail,
  TitleRichDetail,
} from "../types";
import { Link, Loading, Notice, PageTitle, Poster } from "../ui";
import { CheckIcon, PlayIcon, PlusIcon, StarIcon } from "../icons";
import type { WebEpisodesResponse } from "@shared/consumer-tvod";
import { serverNow } from "./MyContent";

/**
 * A title's own page (slice J).
 *
 * Phone first, top to bottom: the backdrop (banner art under a scrim of the
 * page ground), poster + title, ONE metadata row ("2000 • 1 t 50 m • Drama"
 * then the score and age badges), the call to action, Min lista, the
 * synopsis, credits and languages. On a wide screen the poster sits left of
 * the text, over the backdrop.
 *
 * **Every field is shown only when the server sent it** - the API omits what
 * the catalogue has nothing true for (`services/portalTitleDetail.ts`), so
 * there are no empty slots, no zeros and no "undefined" here. A section
 * whose every field is absent is not drawn at all.
 *
 * **No prices**: the per-title shop is retired (organisation plans). A
 * title the member holds says how long is left and plays; a locked one
 * points a visitor to "Ansök om konto" / "Logga in" and a purchaser to its
 * plan, where films are picked.
 *
 * **Min lista** is the member's, on the server. Signed out, the button
 * opens login and comes back here with `?add=list`, which saves the title
 * once and leaves the address bar.
 */

const ADD_PARAM = "add";

function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

/** "Hyrd · utgår om 1 d 4 h" / "Hyrd · starta inom 29 d" / … */
function RentedLine(props: {
  rental: Rental;
  serverTime: string;
  receivedAt: number;
}) {
  const { t } = useI18n();
  const now = useNow();
  const at = serverNow(props.serverTime, props.receivedAt, now);
  const { rental } = props;
  let text: string;
  if (rental.state === "active") {
    if (!rental.endsAt) text = t("title.rentedNoLimit");
    else {
      const left = new Date(rental.endsAt).getTime() - at;
      text =
        left > 0
          ? t("title.rentedEnds", { time: remaining(left, t) })
          : t("title.rentedExpired");
    }
  } else if (rental.state === "not_started") {
    const left = rental.startBy ? new Date(rental.startBy).getTime() - at : 0;
    text =
      rental.startBy && left > 0
        ? t("title.rentedStartWithin", { time: remaining(left, t) })
        : rental.startBy
          ? t("title.rentedExpired")
          : t("title.rentedNoLimit");
  } else {
    text = t("title.rentedExpired");
  }
  return <p className="title-page__rented">{text}</p>;
}

function scoreLabel(
  d: NonNullable<TitleRichDetail["score"]>,
  lang: Lang,
  t: Translate
) {
  const base = t(`title.score.${d.source}`, {
    score: scoreText(d.value, lang),
  });
  return d.votes !== null
    ? `${base} (${t("title.score.votes", { n: count(d.votes, lang) })})`
    : base;
}

/** The one metadata row; null when there is nothing for it. */
function MetaRow({ d }: { d: TitleRichDetail }) {
  const { t, lang } = useI18n();
  const parts: string[] = [];
  if (d.year) parts.push(String(d.year));
  const time = runtime(d.durationSeconds, lang);
  if (time) parts.push(time);
  if (d.series) {
    if (d.series.seasons > 0) {
      parts.push(
        d.series.seasons === 1
          ? t("title.season")
          : t("title.seasons", { n: d.series.seasons })
      );
    }
    if (d.series.episodes > 0) {
      parts.push(t("title.episodes", { n: d.series.episodes }));
    }
  }
  if (d.genres && d.genres.length > 0) {
    parts.push(
      d.genres
        .slice(0, 3)
        .map((g) => g.label)
        .join(", ")
    );
  }
  const score = d.score;
  const age = d.ageRating;
  if (parts.length === 0 && !score && !age) return null;
  return (
    <div className="title-page__meta" data-testid="title-meta">
      {parts.length > 0 && (
        <p className="title-page__facts">
          {parts.map((p, i) => (
            <span key={i}>
              {i > 0 && (
                <span className="title-page__sep" aria-hidden="true">
                  {" • "}
                </span>
              )}
              {p}
            </span>
          ))}
        </p>
      )}
      {score && (
        <span
          className="title-page__badge title-page__badge--score"
          role="img"
          aria-label={scoreLabel(score, lang, t)}
          title={scoreLabel(score, lang, t)}
          data-testid="title-score"
        >
          <StarIcon />
          <span aria-hidden="true">{scoreText(score.value, lang)}</span>
        </span>
      )}
      {age && (
        <span
          className="title-page__badge title-page__badge--age"
          role="img"
          aria-label={
            age.label === "Btl"
              ? t("title.ageAll")
              : t("title.age", { age: age.label.replace("+", "") })
          }
          title={
            age.label === "Btl"
              ? t("title.ageAll")
              : t("title.age", { age: age.label.replace("+", "") })
          }
          data-testid="title-age"
        >
          <span aria-hidden="true">{age.label}</span>
        </span>
      )}
    </div>
  );
}

function ListButton(props: {
  assetId: string;
  on: boolean;
  onChange(on: boolean): void;
}) {
  const { t } = useI18n();
  const { me, go } = usePortal();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const toggle = async () => {
    if (!me) {
      const back = `/titles/${encodeURIComponent(props.assetId)}?${ADD_PARAM}=list`;
      go(`/login?next=${encodeURIComponent(back)}`);
      return;
    }
    setBusy(true);
    setFailed(false);
    const next = !props.on;
    props.onChange(next);
    try {
      if (next) await portalApi.addToList(props.assetId);
      else await portalApi.removeFromList(props.assetId);
    } catch {
      props.onChange(!next);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={
          props.on
            ? "btn btn--ghost title-page__list is-on"
            : "btn btn--ghost title-page__list"
        }
        aria-pressed={props.on}
        aria-label={
          props.on ? t("title.removeFromListLabel") : t("title.addToListLabel")
        }
        disabled={busy}
        onClick={() => void toggle()}
      >
        {props.on ? <CheckIcon /> : <PlusIcon />}
        <span>{props.on ? t("title.onList") : t("title.addToList")}</span>
      </button>
      {failed && (
        <p className="title-page__error" role="alert">
          {t("title.listFailed")}
        </p>
      )}
    </>
  );
}

function Credits({ d }: { d: TitleRichDetail }) {
  const { t, lang } = useI18n();
  const rows: Array<{ label: string; value: string; id: string }> = [];
  if (d.directors?.length)
    rows.push({
      id: "director",
      label: t("title.director"),
      value: d.directors.join(", "),
    });
  if (d.cast?.length)
    rows.push({ id: "cast", label: t("title.cast"), value: d.cast.join(", ") });
  if (d.audioLanguages?.length)
    rows.push({
      id: "audio",
      label: t("title.audio"),
      value: d.audioLanguages.map((c) => languageName(c, lang)).join(", "),
    });
  if (d.subtitleLanguages?.length)
    rows.push({
      id: "subtitles",
      label: t("title.subtitles"),
      value: d.subtitleLanguages.map((c) => languageName(c, lang)).join(", "),
    });
  if (rows.length === 0) return null;
  return (
    <section aria-labelledby="title-details-h" className="title-page__section">
      <h2 id="title-details-h" className="section-title">
        {t("title.details")}
      </h2>
      <dl className="title-page__credits">
        {rows.map((r) => (
          <div
            key={r.id}
            className="title-page__credit"
            data-testid={`credit-${r.id}`}
          >
            <dt>{r.label}</dt>
            <dd>{r.value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** The detail an older API (no `detail`) still lets us draw. */
function fallbackDetail(d: TitleDetail): TitleRichDetail {
  const card = d.title;
  return {
    assetId: card.assetId,
    type: card.type,
    title: card.title,
    ...(card.synopsis ? { synopsis: card.synopsis } : {}),
    images: {
      ...(card.images.banner ? { backdrop: card.images.banner } : {}),
      ...(card.images.poster ? { poster: card.images.poster } : {}),
    },
  };
}

export function TitleScreen({ assetId }: { assetId: string }) {
  const { t, lang } = useI18n();
  const { me, replace } = usePortal();
  const [detail, setDetail] = useState<TitleDetail | null>(null);
  const [error, setError] = useState<"not_found" | "failed" | null>(null);
  const [receivedAt, setReceivedAt] = useState(0);
  const [onList, setOnList] = useState(false);
  const addOnce = useRef(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      let d = await portalApi.title(assetId, lang);
      if (me && !d.signedIn && (await refreshSession())) {
        d = await portalApi.title(assetId, lang);
      }
      setReceivedAt(Date.now());
      setDetail(d);
      setOnList(d.onMyList === true);
    } catch (err) {
      setError(statusOf(err) === 404 ? "not_found" : "failed");
    }
  }, [assetId, lang, me]);

  useEffect(() => {
    void load();
  }, [load]);

  // A series lists its episodes (signed in: each with the member's own
  // access - covered by its own, its season's or its show's rental).
  const isSeries = detail?.title.type === "series" || !!detail?.detail?.series;
  const [episodes, setEpisodes] = useState<WebEpisodesResponse | null>(null);
  useEffect(() => {
    if (!isSeries || !me) return;
    let live = true;
    portalApi
      .episodes(assetId, lang)
      .then((e) => {
        if (live) setEpisodes(e);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [isSeries, me, assetId, lang, detail?.rental?.state]);
  const firstPlayable = episodes?.seasons
    .flatMap((x) => x.episodes)
    .find((e) => e.access === "playable");

  // Back from login with ?add=list: save it once, then tidy the address bar.
  useEffect(() => {
    if (!me || !detail || addOnce.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get(ADD_PARAM) !== "list") return;
    addOnce.current = true;
    replace(`/titles/${encodeURIComponent(assetId)}`);
    if (detail.onMyList) return;
    setOnList(true);
    portalApi.addToList(assetId).catch(() => setOnList(false));
  }, [me, detail, assetId, replace]);

  if (error === "not_found") {
    return (
      <section className="panel">
        <PageTitle>{t("title.notFound")}</PageTitle>
        <Link to="/">{t("return.browse")}</Link>
      </section>
    );
  }
  if (error) {
    return (
      <section className="panel">
        <Notice kind="error">{t("common.error")}</Notice>
        <button type="button" className="btn" onClick={() => void load()}>
          {t("common.retry")}
        </button>
      </section>
    );
  }
  if (!detail) return <Loading />;

  const d = detail.detail ?? fallbackDetail(detail);
  const rented =
    detail.rental && detail.rental.state !== "revoked" && !detail.locked
      ? detail.rental
      : null;
  const backdrop = d.images.backdrop;

  return (
    <article className="title-page" data-testid="title-page">
      <div
        className={
          backdrop
            ? "title-page__hero title-page__hero--image"
            : "title-page__hero"
        }
      >
        {backdrop && (
          <img className="title-page__backdrop" src={backdrop} alt="" />
        )}
        <div className="title-page__scrim" aria-hidden="true" />
        <div className="title-page__head">
          <div className="title-page__poster">
            <Poster src={d.images.poster} title={d.title} />
          </div>
          <div className="title-page__text">
            <PageTitle className="title-page__title">{d.title}</PageTitle>
            {d.originalTitle && (
              <p className="title-page__original">
                {t("title.originalTitle", { title: d.originalTitle })}
              </p>
            )}
            <MetaRow d={d} />
            <div className="title-page__actions">
              {me?.kind === "purchaser" ? (
                // The purchaser never plays (and has no Spela): it adds
                // films to the subscription instead (Sigge 2026-10-07).
                <PurchaserPick assetId={d.assetId} title={d.title} />
              ) : rented ? (
                <div className="title-page__rental" data-testid="title-rented">
                  <RentedLine
                    rental={rented}
                    serverTime={detail.serverTime}
                    receivedAt={receivedAt}
                  />
                  {isSeries && !firstPlayable ? null : (
                    <Link
                      className="btn btn--primary btn--inline title-page__cta title-page__play"
                      to={`/watch/${encodeURIComponent(firstPlayable?.assetId ?? d.assetId)}`}
                    >
                      <PlayIcon className="btn__icon" />
                      {firstPlayable
                        ? t("title.playEpisode", {
                            s: firstPlayable.seasonNumber ?? 1,
                            e: firstPlayable.episodeNumber ?? 1,
                          })
                        : t("title.play")}
                    </Link>
                  )}
                </div>
              ) : detail.locked && !me ? (
                <>
                  <Link
                    className="btn btn--primary btn--inline title-page__cta"
                    to="/account/register"
                  >
                    {t("nav.register")}
                  </Link>
                  <Link
                    className="btn btn--ghost btn--inline"
                    to={`/login?next=${encodeURIComponent(`/titles/${encodeURIComponent(d.assetId)}`)}`}
                  >
                    {t("nav.login")}
                  </Link>
                </>
              ) : null}
              <ListButton
                assetId={d.assetId}
                on={onList}
                onChange={setOnList}
              />
            </div>
          </div>
        </div>
      </div>

      {d.synopsis && (
        <section
          aria-labelledby="title-about-h"
          className="title-page__section"
        >
          <h2 id="title-about-h" className="section-title">
            {d.type === "series"
              ? t("title.aboutSeries")
              : t("title.aboutFilm")}
          </h2>
          <p className="title-page__synopsis">{d.synopsis}</p>
        </section>
      )}

      <Credits d={d} />

      {episodes && episodes.seasons.length > 0 && (
        <Episodes data={episodes} canPlay={me?.kind !== "purchaser"} />
      )}
    </article>
  );
}

/** Season tabs and the episode list; an episode plays when it is covered. */
function Episodes(props: { data: WebEpisodesResponse; canPlay?: boolean }) {
  const { t } = useI18n();
  const seasons = props.data.seasons;
  const [index, setIndex] = useState(0);
  const season = seasons[Math.min(index, seasons.length - 1)]!;
  return (
    <section
      aria-labelledby="episodes-h"
      className="title-page__section episodes"
    >
      <div className="episodes__head">
        <h2 id="episodes-h" className="section-title">
          {t("title.episodeList")}
        </h2>
        {seasons.length > 1 && (
          <div
            className="seasons"
            role="tablist"
            aria-label={t("title.seasonTabs")}
          >
            {seasons.map((x, i) => (
              <button
                key={x.seasonNumber ?? `s${i}`}
                type="button"
                role="tab"
                aria-selected={i === index}
                onClick={() => setIndex(i)}
              >
                {x.title || t("title.seasonN", { n: x.seasonNumber ?? i + 1 })}
              </button>
            ))}
          </div>
        )}
      </div>
      <ol className="episode-list">
        {season.episodes.map((e, i) => {
          const n = e.episodeNumber ?? i + 1;
          const body = (
            <>
              <div className="episode__art">
                {e.image ? <img src={e.image} alt="" loading="lazy" /> : n}
              </div>
              <div className="episode__body">
                <div className="episode__row">
                  <span className="episode__title">
                    {n}. {e.title}
                  </span>
                  {e.durationSeconds ? (
                    <span className="episode__len">
                      {Math.round(e.durationSeconds / 60)} min
                    </span>
                  ) : null}
                </div>
                {e.synopsis ? (
                  <span className="episode__text">{e.synopsis}</span>
                ) : null}
                {e.access !== "playable" && (
                  <span className="episode__state">
                    {e.access === "expired"
                      ? t("title.episodeExpired")
                      : t("title.episodeLocked")}
                  </span>
                )}
              </div>
            </>
          );
          return (
            <li key={e.assetId}>
              {e.access === "playable" && props.canPlay !== false ? (
                <Link
                  className="episode"
                  to={`/watch/${encodeURIComponent(e.assetId)}`}
                >
                  {body}
                </Link>
              ) : (
                <div className="episode">{body}</div>
              )}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/**
 * A purchaser on a title page (Sigge 2026-10-07): no Spela - it cannot play -
 * but "Lägg till", which adds the film to the subscription after a firm
 * "are you sure", since a choice cannot be undone by the purchaser. Already
 * included (picked, or a full-library plan) says so; no picks left points to
 * the upgrade.
 */
function PurchaserPick(props: { assetId: string; title: string }) {
  const { t } = useI18n();
  const [view, setView] = useState<OrgPlanView | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState(false);

  useEffect(() => {
    let live = true;
    portalApi
      .org()
      .then((v) => live && setView(v))
      .catch(() => live && setError(t("common.error")));
    return () => {
      live = false;
    };
  }, [t]);

  if (error && !view) return <Notice kind="error">{error}</Notice>;
  if (!view) return null;

  const key = props.assetId.toLowerCase();
  const included =
    view.plan.films === null ||
    view.picks.some((p) => p.assetId.toLowerCase() === key);

  if (included) {
    return (
      <p className="title-page__included" data-testid="title-included">
        <CheckIcon className="btn__icon" />
        {added ? t("pick.added") : t("pick.included")}
      </p>
    );
  }
  if (!view.picksLeft) {
    return (
      <div className="title-page__pick">
        <p className="muted">{t("pick.noneLeft")}</p>
        <Link className="btn btn--primary btn--inline" to="/plan">
          {t("plan.upgradeTitle")}
        </Link>
      </div>
    );
  }
  async function add() {
    setBusy(true);
    setError(null);
    try {
      setView(await portalApi.orgPick([props.assetId]));
      setAdded(true);
    } catch (err) {
      setError(errorText(err, t));
    } finally {
      setBusy(false);
      setConfirming(false);
    }
  }
  return (
    <div className="title-page__pick">
      {confirming ? (
        <div
          className="pick-confirm"
          role="alertdialog"
          aria-labelledby="pick-confirm-h"
        >
          <p id="pick-confirm-h" className="pick-confirm__q">
            {t("pick.confirmTitle", { title: props.title })}
          </p>
          <p className="pick-confirm__body">
            {t("pick.confirmBody", { after: view.picksLeft - 1 })}
          </p>
          <div className="pick-confirm__actions">
            <button
              type="button"
              className="btn btn--primary"
              disabled={busy}
              onClick={() => void add()}
            >
              {t("pick.confirmYes")}
            </button>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setConfirming(false)}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn--primary btn--inline title-page__cta"
          onClick={() => setConfirming(true)}
        >
          <PlusIcon className="btn__icon" />
          {t("pick.add")}
        </button>
      )}
      {error && <Notice kind="error">{error}</Notice>}
      <p className="muted">{t("pick.left", { left: view.picksLeft })}</p>
    </div>
  );
}
